import { Trans, useTranslation } from 'react-i18next';
import type { Namespace } from '@sib/i18n';
import { Highlight } from '@sib/ui';
import {
  EntityActivityLogActionKeys,
  EntityActivityLogDescription as EntityActivityLogDescriptionModel,
  EntityActivityLogDescriptionKind,
} from './entity-activity-log.types';

interface EntityActivityLogDescriptionProps {
  description: EntityActivityLogDescriptionModel;
  actionKeys: EntityActivityLogActionKeys;
  ns: Namespace;
}

export const EntityActivityLogDescription = ({
  description,
  actionKeys,
  ns,
}: EntityActivityLogDescriptionProps) => {
  const { t } = useTranslation(ns);

  switch (description.kind) {
    case EntityActivityLogDescriptionKind.FieldChanged:
      return (
        <Trans
          t={t}
          i18nKey={actionKeys[EntityActivityLogDescriptionKind.FieldChanged]}
          values={{
            fieldLabel: description.fieldLabel,
            oldValue: description.oldValue,
            newValue: description.newValue,
          }}
          components={{ strong: <Highlight /> }}
        />
      );
    case EntityActivityLogDescriptionKind.FieldToggled: {
      const toggleKeys =
        actionKeys[EntityActivityLogDescriptionKind.FieldToggled];
      const hasName = !!description.itemType && !!description.name;

      const i18nKey = description.enabled
        ? (hasName && toggleKeys.onWithName) || toggleKeys.on
        : (hasName && toggleKeys.offWithName) || toggleKeys.off;

      return (
        <Trans
          t={t}
          i18nKey={i18nKey}
          values={{
            fieldLabel: description.fieldLabel,
            itemType: description.itemType,
            name: description.name,
          }}
          components={{ strong: <Highlight /> }}
        />
      );
    }
    case EntityActivityLogDescriptionKind.RelatedItemAdded:
      return (
        <Trans
          t={t}
          i18nKey={
            actionKeys[EntityActivityLogDescriptionKind.RelatedItemAdded]
          }
          values={{ itemType: description.itemType, name: description.name }}
          components={{ strong: <Highlight /> }}
        />
      );
    case EntityActivityLogDescriptionKind.RelatedItemDeleted:
      return (
        <Trans
          t={t}
          i18nKey={
            actionKeys[EntityActivityLogDescriptionKind.RelatedItemDeleted]
          }
          values={{ itemType: description.itemType, name: description.name }}
          components={{ strong: <Highlight /> }}
        />
      );
    case EntityActivityLogDescriptionKind.EntityCreated:
      return (
        <Trans
          t={t}
          i18nKey={actionKeys[EntityActivityLogDescriptionKind.EntityCreated]}
          components={{ strong: <Highlight /> }}
        />
      );
    default:
      return null;
  }
};
